import { uuidv4 } from "./uuidv4";
import { flatten, unflatten } from "flat";
import { getAuthData } from "./getAuthData";
import { datadog as config, env as environment } from "../../config";
import Constants from "expo-constants";
import * as Device from "expo-device";
import { Platform } from "react-native";

const OBFUSCATION_MASK = Object.freeze("******");
const OBFUSCATION_KEYS = ["headers.authorization", "accessToken", "userToken"];

interface ILoggerParams {
  userContext?: object;
}

export function getLogger({ userContext }: ILoggerParams) {
  if (!config.clientToken || !config.isEnabled) return console;
  return new DataDogLogger({ userContext });
}

export class DataDogLogger {
  private token: string;
  private userContext: any;
  public service: string | undefined;
  public url: string;
  private traceId: string;
  private configEnv: string;

  constructor({ userContext }: ILoggerParams) {
    this.token = config.clientToken;
    this.configEnv = environment;
    this.url = `https://browser-http-intake.logs.datadoghq.com/v1/input/${this.token}`;
    this.userContext = userContext;
    this.service = Platform?.OS === "web" ? "ResidentWeb" : "ResidentMobile";
    this.traceId = uuidv4();
  }

  private async send(log: any, level: "log" | "info" | "error" | "warn" | "debug") {
    const body = {
      message: log,
      ddsource: "react-native-app",
      ddtags: `env:${this.configEnv}`,
      ddtrace: this.traceId,
      service: this.service,
      timestamp: new Date().getTime(),
      deviceInfo: {
        AppVersion: Constants?.expoConfig?.version,
        OS: Device?.osName,
        OsVersion: Device?.osVersion,
        ModelName: Device?.modelName,
        Platform: Platform?.OS === "web" ? "web" : "mobile",
      },
      level,
    };
    if (level === "error") {
      body.error = log;
    }

    await fetch(this.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })
      .catch(this.logError)
      .then(() => {
        if (level === "error") {
          this.logError(log);
        } else {
          if (environment !== "prd") {
            console[level](log);
          }
        }
      });
  }

  public log(msg: string): void {
    this.send(this.stringify(msg), "log");
  }

  public logResponse(callee: string, resp: object): void {
    let msg = this.stringify(`${callee} returned`, resp);
    this.send(msg, "log");
  }

  public logRequest(endpoint: string, params?: object) {
    let msg = `Making request to ${endpoint}`;
    this.send({ msg, params }, params ? "log" : "info");
  }

  public info(msg: string, params?: object): void {
    this.send(this.stringify(msg, params), "info");
  }

  public infoObject(msg: object): void {
    this.send(msg, "info");
  }

  public warn(msg: string): void {
    this.send(msg, "warn");
  }

  public error(error: NodeJS.ErrnoException): void {
    this.logError(error.message);
    this.send(error, "error");
  }

  public hasUserContext() {
    let ctx = this.userContext && this.userContext.userInfo && this.userContext.userInfo;
    return ctx && ctx.id && typeof ctx !== "string";
  }

  public setUserContext(userContext: object): void {
    this.userContext = userContext;
  }

  public logExtraction(value: any, source: "string") {
    this.log(`Extracted value ${this.stringify(value)} from ${source}`);
  }

  public stringify(str: string, params?: object) {
    if (params) {
      const obfuscatedParams = this.obfuscate(params);
      str += ` Params ${JSON.stringify(obfuscatedParams, null, 2)}`;
    }

    return str;
  }

  private obfuscate(obj: object) {
    const flattenObject: any = flatten(obj);

    OBFUSCATION_KEYS.forEach((key) => {
      if (flattenObject[key]) {
        flattenObject[key] = OBFUSCATION_MASK;
      }
    });

    return unflatten(flattenObject);
  }

  // After upgrading to Expo SDK 47 Expo Go on ios crashes when calling console.error("")
  // https://domuso.atlassian.net/browse/ENG-1951
  private logError(msg: any) {
    //Only change to log if is a real ios device, and is not production.
    if (Device.isDevice && Device?.osName === "iOS" && environment !== "prd") {
      console.log(msg);
    } else {
      console.error(msg);
    }
  }
}
